import {Injectable} from '@angular/core';
import {UsersService} from '../api/services/users.service';
import {ContactService as ApiContactService} from '../api/services/contact.service';
import {TagsService as ApiTagsService} from '../api/services/tags.service';
import {
  ContactModel, ContactsImportResponse,
  ContactsRequest, ContactsResponse,
  ContactSyncModel, CustomFieldDefinitionsRequest, CustomFieldModel,
  DuplicateContactModel,
  DuplicateContactSummaryModel,
  SaveContactsRequest,
  FileUploadModel,
  LookupModel,
  TagModel,
} from '../api/models';
import {AuthService} from '../core/security/auth.service';
import {RolesEnum} from '../core/security/roles.enum';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {ApiConfiguration} from '../api/api-configuration';
import {FilterOptionModel} from '../dialogs/grid-filter/filter-option.model';
import {ModulesEnum} from '../core/security/modules.enum';

@Injectable()
export class ContactService {

  public selectedDirectory = -1;
  public directoryList = [
    {label: 'All Directories', value: -1},
    {label: 'Company Directory', value: 0},
    {label: 'Private Directory', value: 1},
  ];

  public directoryEditList: LookupModel[] = [
    {text: 'Company Directory', id: 0},
    {text: 'Private Directory', id: 1},
  ];

  public smsPhoneTypeList: LookupModel[] = [
    {text: 'Phone Number', id: 'phoneNumber'},
    {text: 'Mobile Number', id: 'mobileNumber'},
    {text: 'Fax Number', id: 'faxNumber'},
  ];

  public communicationTypes: LookupModel[] = [
    {text: 'SMS/MMS', id: 0},
    {text: 'WhatsApp', id: 1},
  ];

  allTags: TagModel[] = [];
  customFields: Array<CustomFieldModel>;

  hasTagsViewAccess = false;
  hasCustomFieldsModule = false;

  public hasPublicContactsViewAccess = false;
  public hasPrivateContactsViewAccess = false;

  public hasPublicContactsEditAccess = false;
  public hasPrivateContactsEditAccess = false;

  public hasPublicContactsDeleteAccess = false;
  public hasPrivateContactsDeleteAccess = false;

  constructor(private apiUserService: UsersService,
              private apiContactService: ApiContactService,
              private apiTagsService: ApiTagsService,
              private authService: AuthService,
              private httpClient: HttpClient,
              private apiConfig: ApiConfiguration,
  ) {

    this.authService.currentUser.subscribe((user) => {

      this.hasPublicContactsViewAccess = this.authService.hasAccess([
        RolesEnum.CompanyContact_View,
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete
      ]);
      this.hasPrivateContactsViewAccess = this.authService.hasAccess([
        RolesEnum.PrivateContact_View,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete
      ]);

      this.hasPublicContactsEditAccess = this.authService.hasAccess([
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete
      ]);
      this.hasPrivateContactsEditAccess = this.authService.hasAccess([
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete
      ]);

      this.hasPublicContactsDeleteAccess = this.authService.hasAccess([
        RolesEnum.CompanyContact_Delete
      ]);
      this.hasPrivateContactsDeleteAccess = this.authService.hasAccess([
        RolesEnum.PrivateContact_Delete
      ]);

      this.hasTagsViewAccess = this.authService.hasAccess([RolesEnum.Tags_View, RolesEnum.Tags_Edit, RolesEnum.Tags_Delete])
        && this.authService.userHasPackage(ModulesEnum.TagsModule);
      this.hasCustomFieldsModule = this.authService.userHasPackage([ModulesEnum.CustomFieldsModule]);

      this.directoryList = [];
      this.directoryEditList = [];
      if (this.hasPublicContactsViewAccess && this.hasPrivateContactsViewAccess) {
        this.selectedDirectory = -1;
        this.directoryList.push({label: 'All Directories', value: -1});
      }
      if (this.hasPublicContactsViewAccess) {
        if (this.hasPublicContactsViewAccess && !this.hasPrivateContactsViewAccess) {
          this.selectedDirectory = 0;
        }
        this.directoryList.push({label: 'Company Directory', value: 0});
      }
      if (this.hasPrivateContactsViewAccess) {
        if (this.hasPrivateContactsViewAccess && !this.hasPublicContactsViewAccess) {
          this.selectedDirectory = 1;
        }
        this.directoryList.push({label: 'Private Directory', value: 1});
      }

      if (this.hasPublicContactsEditAccess) {
        this.directoryEditList.push({text: 'Company Directory', id: 0});
      }
      if (this.hasPrivateContactsEditAccess) {
        this.directoryEditList.push({text: 'Private Directory', id: 1});
      }
    });
  }

  public async queryContacts(state: ContactsRequest): Promise<ContactsResponse> {
    return await this.apiContactService.contactGetUserContacts({body: state}).toPromise();
  }

  public async getDuplicateUserContactsSummary(): Promise<Array<DuplicateContactSummaryModel>> {
    const response = await this.apiContactService.contactGetDuplicateUserContactsSummary().toPromise();

    return response.data;
  }

  public async getDuplicateUserContactsList(contactType: string): Promise<Array<DuplicateContactModel>> {
    console.log('getDuplicateUserContactsList', contactType);
    const response = await this.apiContactService.contactGetDuplicateUserContacts({
      body: {
        contactType: contactType?.toString()
      }
    }).toPromise();

    return response.data;
  }

  public async getTags(): Promise<TagModel[]> {
    const response = await this.apiTagsService.tagsGetTags().toPromise();
    this.allTags = response;

    return response;
  }

  public async getContactById(id: string): Promise<ContactModel> {
    return await this.apiContactService.contactGetContactById({id: id}).toPromise();
  }

  public async saveContact(model: ContactModel): Promise<boolean> {
    await this.apiContactService.contactSaveContact({body: model}).toPromise();

    return true;
  }

  public async saveContacts(model: SaveContactsRequest): Promise<boolean> {
    await this.apiContactService.contactSaveContacts({body: model}).toPromise();

    return true;
  }

  public async deleteContact(id: string): Promise<boolean> {
    await this.apiContactService.contactDeleteContactById({id: id}).toPromise();

    return true;
  }

  public async getCustomFieldsDefinitions(filter?: CustomFieldDefinitionsRequest): Promise<Array<CustomFieldModel>> {
    return await this.apiContactService.contactGetCustomFieldsDefinitions({
      body: filter ?? {}
    }).toPromise();
  }

  public async getIntegrationSettings(): Promise<ContactSyncModel[]> {
    return await this.apiContactService.contactSyncGetSyncSettings().toPromise();
  }

  public async saveSettings(model: ContactSyncModel): Promise<boolean> {
    return await this.apiContactService.contactSyncSaveSyncSettings({body: model}).toPromise();
  }

  public async revokeGoogleIntegration(): Promise<boolean> {
    return await this.apiContactService.contactSyncRevokeGoogleToken().toPromise();
  }

  public async saveGoogleAuthCode(code: string): Promise<boolean> {
    return await this.apiContactService.contactSyncSaveGoogleToken({code: code}).toPromise();
  }

  public async saveMicrosoftAuthCode(code: string): Promise<boolean> {
    return await this.apiContactService.contactSyncSaveMicrosoftToken({code: code}).toPromise();
  }

  public async revokeMicrosoftIntegration(): Promise<boolean> {
    return await this.apiContactService.contactSyncRevokeMicrosoftToken().toPromise();
  }

  public async syncContacts(provider: string): Promise<boolean> {
    const response = await this.apiContactService.contactSyncSyncPbxContactsByProvider({provider: provider}).toPromise();

    return !response.error;
  }

  public async importContacts(data: FileUploadModel, file: File): Promise<ContactsImportResponse> {
    return await this.importCsv(data, file);
  }

  public async importCsv(data: FileUploadModel, file: File): Promise<ContactsImportResponse> {

    const options: any = {
      method: 'post',
      url: this.apiConfig.rootUrl + `/api/contact/import-csv`,
      params: new HttpParams(),
      headers: new HttpHeaders()
    };
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    formData.append('importMergeType', data.importMergeType);
    formData.append('importMergeByMobilePhone', data.importMergeByMobilePhone.toString());
    formData.append('importMergeByEmailAddress', data.importMergeByEmailAddress.toString());

    return this.httpClient.post<ContactsImportResponse>(options.url, formData, {})
      .toPromise()
      .then(response => {
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  async initFilterOptions(): Promise<FilterOptionModel[]> {
    if (this.hasTagsViewAccess) {
      this.allTags = await this.getTags();
    }

    if (this.hasCustomFieldsModule) {
      this.customFields = await this.getCustomFieldsDefinitions({searchOnly: true});
    }

    const filterOptions: FilterOptionModel[] = [
      {
        filterKey: 'searchText',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Any'
      },
      {
        filterKey: 'firstName',
        multiValue: false,
        showAlways: true,
        title: 'First Name',
        type: 'string',
      },
      {
        filterKey: 'lastName',
        multiValue: false,
        showAlways: true,
        title: 'Last Name',
        type: 'string',
      },
      {
        filterKey: 'phoneNumber',
        multiValue: false,
        showAlways: true,
        title: 'Phone Number',
        type: 'phoneNumber',
      },
      {
        filterKey: 'mobileNumber',
        multiValue: false,
        showAlways: true,
        title: 'Mobile Number',
        type: 'phoneNumber',
      },
      {
        filterKey: 'faxNumber',
        multiValue: false,
        showAlways: true,
        title: 'Fax Number',
        type: 'phoneNumber',
      },
      {
        filterKey: 'companyName',
        multiValue: false,
        showAlways: true,
        title: 'Company Name',
        type: 'string',
      },
      {
        filterKey: 'email',
        multiValue: false,
        showAlways: true,
        title: 'Email',
        type: 'string',
      },
      {
        filterKey: 'position',
        multiValue: false,
        showAlways: true,
        title: 'Title',
        type: 'string',
      },
      {
        filterKey: 'speedDial',
        multiValue: false,
        showAlways: true,
        title: 'Speed Dial',
        type: 'phoneNumber',
      },
      {
        filterKey: 'contactType',
        multiValue: false,
        showAlways: false,
        type: 'static',
        title: 'Directory',
        displayValue: 'Company',
        value: '0',
        searchText: 'Directory: Company',
      },
      {
        filterKey: 'contactType',
        multiValue: false,
        showAlways: false,
        type: 'static',
        title: 'Directory',
        displayValue: 'Private',
        value: '1',
        searchText: 'Directory: Private',
      },
      {
        filterKey: 'contactType',
        multiValue: false,
        showAlways: false,
        type: 'static',
        title: 'Directory',
        displayValue: 'Internal',
        value: '2',
        searchText: 'Directory: Internal',
      }
    ];

    if (this.allTags?.length > 0) {
      for (const tag of this.allTags) {
        filterOptions.push({
          filterKey: 'tags',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Tag',
          displayValue: tag.name,
          value: tag.id,
          searchText: `Tag: ${tag.name}`,
        });
      }
    }

    if(this.customFields?.length > 0) {
      for (const customField of this.customFields) {
        if(customField.fieldType == 'text') {
          filterOptions.push({
            filterKey: `customField.contains.${customField.fieldKey}`,
            multiValue: false,
            showAlways: true,
            type: 'string',
            title: customField.fieldTitle,
          });
        } else if(customField.fieldType == 'number') {
          filterOptions.push({
            filterKey: `customField.equals.${customField.fieldKey}`,
            multiValue: false,
            showAlways: true,
            type: 'number',
            operator: 'equals',
            title: `${customField.fieldTitle} equals`,
          });
          filterOptions.push({
            filterKey: `customField.greaterThan.${customField.fieldKey}`,
            multiValue: false,
            showAlways: true,
            type: 'number',
            operator: 'greaterThan',
            title: `${customField.fieldTitle} greater than`,
          });
          filterOptions.push({
            filterKey: `customField.lessThan.${customField.fieldKey}`,
            multiValue: false,
            showAlways: true,
            type: 'number',
            operator: 'lessThan',
            title: `${customField.fieldTitle} less than`,
          });
        } else if(customField.fieldType == 'boolean') {
          filterOptions.push({
            filterKey: `customField.equals.${customField.fieldKey}`,
            multiValue: false,
            showAlways: false,
            displayValue: 'Yes',
            value: true,
            type: 'static',
            title: customField.fieldTitle,
            searchText: `${customField.fieldTitle}: Yes`,
          });

          filterOptions.push({
            filterKey: `customField.equals.${customField.fieldKey}`,
            multiValue: false,
            showAlways: false,
            displayValue: 'No',
            value: true,
            type: 'static',
            title: customField.fieldTitle,
            searchText: `${customField.fieldTitle}: No`,
          });
        } else if(customField.fieldType == 'date') {
          filterOptions.push({
            filterKey: `customField.equals.${customField.fieldKey}`,
            multiValue: false,
            showAlways: true,
            type: 'date',
            operator: 'equals',
            title: `${customField.fieldTitle} equals`,
          });
          filterOptions.push({
            filterKey: `customField.greaterThan.${customField.fieldKey}`,
            multiValue: false,
            showAlways: true,
            type: 'date',
            operator: 'greaterThan',
            title: `${customField.fieldTitle} greater than`,
          });
          filterOptions.push({
            filterKey: `customField.lessThan.${customField.fieldKey}`,
            multiValue: false,
            showAlways: true,
            type: 'date',
            operator: 'lessThan',
            title: `${customField.fieldTitle} less than`,
          });
        } else if(customField.fieldType == 'list') {
          filterOptions.push({
            filterKey: `customField.equals.${customField.fieldKey}`,
            multiValue: true,
            showAlways: true,
            type: 'string',
            title: customField.fieldTitle,
          });
        }
      }
    }

    return filterOptions;
  }
}
